const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/actions/kisapi/metaKat.ts';
import Imm from 'immutable';
import moment from 'moment';
import { Logger } from '../../services/KisLoggerService';
import * as Utils from '../../utils/generalUtils';
import { compileEndpoint, warnWhenStringifyRemovesAttributes } from './utils';

const Log = Logger('kisapi/metaKat');

/**
 * Sets of MetaKat fields.
 */
export enum MetaKatFields {
    ALL_EXCLUDING_CONTENT,
    ALL_INCLUDING_CONTENT,
}

/**
 * Helper function to build a string containing a list of MetaKat fields.
 * @param fields the fields to build the string from
 * @return: The list of fields, delimited by asterisks.
 */
function buildMetaKatFields(fields: string[] | MetaKatFields): string {
    const basic =
        'ICODE*PCODE*REF*' + // primary key and self-referencing keys
        'KAT*FKEY*TYP*' + // three levels of defining the type of record
        'NAME*POS*CHILD*' + // basic display info
        'VISUM*DAT*MUTVIS*MUTDAT*' + // user and timestamp of creation/edit
        'KATRCODE*SELRCODE*' + // access rights
        'STATUS*STARTDAT*STOPDAT*' + // visibility
        'PARAMICODE'; // QSCRIPT form

    switch (fields) {
        case MetaKatFields.ALL_EXCLUDING_CONTENT:
            return basic;
        case MetaKatFields.ALL_INCLUDING_CONTENT:
            return `${basic}*CONTENT`;
        default:
            return (fields as string[]).join('*');
    }
}

/**
 * Retrieves the PersData subtree belonging to the given user
 */
export function apiLoadMetaKatPersData(visum: string) {
    return {
        endpoint: compileEndpoint(
            ['MetaKat', 'PersData', visum || '?'], //
            null
        ),
        requestConfig: {
            cache: 'force-cache',
        },
    };
}

/**
 * @deprecated
 * FIXME remove this action; callers should use apiLoadMetaKatBlock instead
 */
export function apiLoadUserPrefsBlock(icode: string) {
    return {
        endpoint: compileEndpoint(
            ['MetaKat', icode || '?', 'Block'], //
            null
        ),
        requestConfig: {
            cache: 'force-cache',
        },
    };
}

/**
 * Generic action to load the immediate children of a particular record
 * identified by PCODE. The reply will NOT include the root record.
 *
 * NOTE: If you need the records' ETAGs, pass a minimal set of fields to the
 * fields argument, await the reply, and make a subsequent call to
 * loadMetaKatRecord for each child retrieved!
 *
 * @param pcode (in): The ICODE of the record whose immediate children are to be fetched
 * @param fields (in): The fields to be included in the reply.
 * @param lookup
 */
export function apiLoadMetaKatChildren(pcode: string, fields: string[] | MetaKatFields, lookup: boolean) {
    return {
        endpoint: compileEndpoint(
            ['MetaKat', lookup && 'Lookup', pcode || '?', 'Children'],
            [{ key: 'Fields', value: buildMetaKatFields(fields) }]
        ),
    };
}

/**
 * Action to look for a record with a given KAT and FKEY in a certain subtree of the MetaKat.
 * Note that the action will fail to find the record if the given combination of KAT and FKEY is not unique!
 * @param pcode root ICODE of subtree to search
 * @param kat look for records with this KAT
 * @param fkey look for records with this FKEY; may NOT contain any wildcards
 * @param checkDate true=pass an extra argument FORDAT to check a record's STARTDAT and STOPDAT
 * @param lookup true=look up the hospital-specific pcode
 * @param disallowArrays true=never attempt to convert QSTRUCTs to arrays
 * @param fields the fields to be included in the reply
 * @param status (optional)
 */
export function apiLoadMetaKatRecordByKatFkey(
    pcode: string,
    kat: string,
    fkey: string,
    checkDate: boolean = false,
    lookup: boolean = false,
    disallowArrays: boolean = true,
    fields: string[] | MetaKatFields = MetaKatFields.ALL_EXCLUDING_CONTENT,
    status?: string
) {
    let requestConfig: any;

    if (disallowArrays) {
        requestConfig = {
            headers: { Accept: 'array=no' },
        };
    }

    return {
        endpoint: compileEndpoint(
            ['MetaKat', lookup && 'Lookup', pcode || '?', kat || '?', fkey || ''],
            [
                { key: 'Fields', value: buildMetaKatFields(fields) },
                status && { key: 'STATUS', value: status },
                checkDate && { key: 'FORDAT', value: moment().format('YYYY-MM-DD') },
            ]
        ),
        requestConfig,
    };
}

/**
 * @deprecated
 * FIXME remove this action; callers should use apiLoad1MetaKatRecord instead
 */
export function apiLoadMetaKatByLookup(icode: string) {
    return {
        endpoint: compileEndpoint(
            ['MetaKat', 'Lookup', icode || '?'], //
            [{ key: 'Fields', value: 'ICODE' }]
        ),
    };
}

/**
 * Generic action to load a complete block of a particular record
 * identified by PCODE. The reply WILL include the root record, also an additional field 'LEVEL'
 * which is '1' for the root record and then subsequently increased.
 *
 * NOTE: If you need the records' ETAGs, pass a minimal set of fields to the
 * fields argument, await the reply, and make a subsequent call to
 * loadMetaKatRecord for each child retrieved!
 *
 * @param icode The ICODE of the record to be fetched.
 * @param fields The fields to be included in the reply.
 * @param lookup Use metakat lookp feature
 * @param disallowArrays true=never attempt to convert QSTRUCTs to arrays
 */
export function apiLoadMetaKatBlock(
    icode: string,
    fields: string[] | MetaKatFields,
    lookup: boolean = false,
    disallowArrays: boolean = false,
    transformRtf: boolean = false
) {
    let requestConfig: any;

    if (disallowArrays) {
        requestConfig = {
            headers: { Accept: 'array=no' },
        };
    }

    return {
        endpoint: compileEndpoint(
            ['MetaKat', lookup && 'Lookup', icode || '?', 'Block'],
            [
                { key: 'Fields', value: `${buildMetaKatFields(fields)}*LEVEL` },
                transformRtf && { key: 'Trf', value: 'RtfToAnsi' },
            ]
        ),
        requestConfig,
    };
}

/**
 * Generic action to load a single record from MetaKat.
 *
 * @param icode The ICODE of the record to be loaded.
 * @param fields Array of fields you're interested in. If omitted, the
 *    action will load all the fields except CONTENT. If an empty array is
 *    passed in, the action will load all the fields including CONTENT.
 * @param lookup
 * @param disallowArrays
 */
export function apiLoad1MetaKatRecord(
    icode: string,
    fields: string[] | MetaKatFields,
    lookup: boolean = false,
    disallowArrays: boolean = false,
    transformRtf: boolean = false
) {
    let requestConfig: any;

    if (disallowArrays) {
        requestConfig = {
            headers: { Accept: 'array=no' },
        };
    }

    return {
        endpoint: compileEndpoint(
            ['MetaKat', lookup && 'Lookup', icode || '?'],
            [
                { key: 'Fields', value: buildMetaKatFields(fields) },
                transformRtf && { key: 'Trf', value: 'RtfToAnsi' },
            ]
        ),
        requestConfig,
    };
}

/**
 * Generic action to load the access rights of a single MetaKat record.
 * @param icode The ICODE of the record whose rights are to be queried.
 */
export function apiLoadMetaKatRecordRights(icode: string) {
    return {
        endpoint: compileEndpoint(['MetaKat', icode || '?', 'Rights'], null),
    };
}

/**
 * Generic action to insert a new record into the MetaKat.
 *
 * WARNING: Never, EVER pass an arbitrary value for PCODE or ICODE! Doing
 * so would break the MetaKat's tree structure! Always pass a valid PCODE
 * or ICODE as obtained from a previous call to KISAPI!
 *
 * @param rec Must contain at least: PCODE, KAT. Must not contain one
 *   or more of: ICODE, DAT, VISUM, MUTDAT, MUTVIS. Note that any unknown
 *   member will be silently ignored; generic data *must* be put under
 *   CONTENT!
 */
export function apiInsertMetaKatRecord(rec: Imm.Map<string, any>) {
    Utils.checkMandatoryMembers(rec, ['PCODE', 'KAT']);
    Utils.checkEmptyMembers(rec, ['ICODE', 'DAT', 'VISUM', 'MUTDAT', 'MUTVIS']);

    if (!rec.get('NAME')) {
        const kat = rec.get('KAT');
        const fkey = rec.get('FKEY');
        rec = rec.set('NAME', fkey || kat || '?');
    }

    if (!rec.get('STATUS')) {
        rec = rec.set('STATUS', 'A'); // A=active
    }

    if (!rec.get('PARAMICODE')) {
        rec = rec.set('PARAMICODE', 'CFGCONT01'); // a generic QSCRIPT form
    }

    warnWhenStringifyRemovesAttributes(rec);

    return {
        endpoint: compileEndpoint(
            ['MetaKat'], // insert always happens on the top level; PCODE identifies subtree
            null
        ),
        requestConfig: {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(rec.toJS()),
        },
    };
}

/**
 * Generic action to update an existing MetaKat record.
 *
 * WARNING: Never, EVER pass an arbitrary value for PCODE or ICODE! Doing so would break the MetaKat's tree structure!
 * Always pass a valid PCODE or ICODE as obtained from a previous call to KISAPI!
 *
 * @param rec Must contain at least: ICODE, ETAG. The ETAG is needed for concurrency checks, and must be obtained from
 *    a previous call to loadMetaKatRecord or similar endpoint. Top-level members such as PCODE, KAT, NAME may be
 *    omitted; such omitted members will be left untouched. However, you must always pass the entire CONTENT if you
 *    pass any; the endpoint does not merge subtrees of CONTENT.
 */
export function apiUpdateMetaKatRecord(rec: Imm.Map<string, any>) {
    Utils.checkMandatoryMembers(rec, ['ICODE', 'ETAG']);

    const icode = rec.get('ICODE');
    const etag = rec.get('ETAG');

    warnWhenStringifyRemovesAttributes(rec);

    return {
        endpoint: compileEndpoint(['MetaKat', icode || '?'], null),
        requestConfig: {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                'if-match': etag,
            },
            body: JSON.stringify(rec.toJS()),
        },
    };
}

/**
 * Generic function to delete a MetaKat record
 * @param icode Icode to identify the record to delete
 * @param etag Etag of the recorde to delete
 */
export function apiDeleteMetaKatRecord(icode: string, etag: string) {
    if (!icode || !etag) {
        Log.error('deleteMetaKatRecord: Missing mandatory argument icode or etag');
    }

    return {
        endpoint: compileEndpoint(['MetaKat', icode || '?'], null),
        requestConfig: {
            method: 'DELETE',
            headers: {
                'content-type': 'application/json',
                'if-match': etag,
            },
        },
    };
}

/**
 * Generic action to load a multiple records from MetaKat.
 *
 * @param list Array of metakat paths you want to load. This can contain multiple metakat entries.
 * E.g. [
 *    "b6fbdad6-35fd-4716-88ff-c1d64ccdea2c",
 *    "Lookup/b55f8abb-4e94-481b-9742-95a155ced76a",
 *    "b6fbdad6-35fd-4716-88ff-c1d64ccdea2c/Block"
 * ]
 * @param fields Array of fields you're interested in. If omitted, the
 *    action will load all the fields except CONTENT. If an empty array is
 *    passed in, the action will load all the fields including CONTENT.
 */
export function apiMultiLoadMetaKatRecords(list: string[], fields: string[]) {
    return {
        endpoint: compileEndpoint(
            ['MetaKat', 'Multiload'],
            [
                {
                    key: 'Fields',
                    value: `${buildMetaKatFields(fields)}*LEVEL`,
                },
            ]
        ),
        requestConfig: {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ List: list }),
        },
    };
}

/**
 * Loads the immediate children of one of the subtrees of the user's PersData MetaKat tree. The
 * call will return with failure if the given user does not have a record of the given type yet.
 * @param branch The key of the branch whose children are to be read.
 * @param visum The user whose PersData subtree is to be read.
 * @param fields The fields to be included in the reply.
 */
export function apiLoadMetaKatPersDataChildren(branch: string, visum: string, fields: string[] | MetaKatFields) {
    return {
        endpoint: compileEndpoint(
            ['MetaKat', 'PersData', visum || '?', branch || '?'],
            [{ key: 'Fields', value: buildMetaKatFields(fields) }]
        ),
    };
}

/**
 * Inserts a new record into the user's PersMobile subtree of the user's
 * PersData MetaKat tree. If the user hasn't got any PersMobile root record
 * yet, the root record will be created, too.
 * @param branch
 * @param visum The visum of the user into whose PersMobile subtree a new
 *    record is to be inserted.
 * @param rec Must contain at least: KAT, FKEY. Must not contain one or
 *    more of: PCODE, ICODE, DAT, VISUM, MUTDAT, MUTVIS. Note that any unknown
 *    member will be silently ignored; generic data *must* be put under CONTENT!
 */
export function apiInsertMetaKatPersDataRecord(branch: string, visum: string, rec: Imm.Map<string, any>) {
    Utils.checkMandatoryMembers(rec, ['KAT', 'FKEY']);
    Utils.checkEmptyMembers(rec, ['PCODE', 'ICODE', 'DAT', 'VISUM', 'MUTDAT', 'MUTVIS']);

    if (!rec.get('NAME')) {
        const kat = rec.get('KAT');
        const fkey = rec.get('FKEY');
        rec = rec.set('NAME', `${visum} ${kat} ${fkey}`);
    }

    if (!rec.get('STATUS')) {
        rec = rec.set('STATUS', 'A'); // A=active
    }

    if (!rec.get('PARAMICODE')) {
        rec = rec.set('PARAMICODE', 'CFGCONT01'); // a generic QSCRIPT form
    }

    warnWhenStringifyRemovesAttributes(rec);

    return {
        endpoint: compileEndpoint(
            ['MetaKat', 'PersData', visum || '?', branch || '?'], //
            null
        ),
        requestConfig: {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(rec.toJS()),
        },
    };
}

/**
 * Retrieves the favorite patients either on the top level of the user with the given `visum` or in
 * the folder with `ICODE === pcode`
 */
export function apiLoadMetaKatPatFavsOfUser(visum: string, pcode?: string) {
    return {
        endpoint: compileEndpoint(
            [
                'MetaKat',

                // When no pcode was given, we fetch the user's PersPat record:
                !pcode && 'PersData',
                !pcode && (visum || '?'),
                !pcode && 'PersPat',

                // When a pcode was given, we fetch that record's children instead:
                pcode || null,
                pcode && 'Children',
            ],
            null
        ),
    };
}

/**
 * Adds the case with the given casenr (fallnr) to the favorites of the user. The favorite is added
 * at to the favorites at the top level.
 * @param visum Visum of the user using the app.
 * @param casenr Case number (fallnr) to identify the case.
 */
export function apiSetCaseAsFavoriteInPersData(visum: string, casenr: string) {
    return {
        endpoint: compileEndpoint(
            ['MetaKat', 'PersData', visum || '?', 'PersPat'], //
            null
        ),
        requestConfig: {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ FALLNR: casenr }),
        },
    };
}
